import { DetailEvent, Entity, registerSystem, System } from "aframe";
import { AvatarActionFactory, AvatarActionInstance, AvatarComponent, AvatarSystem } from "./avatar";
import { InputActionDetail } from "./input/common";
import { SlotEvent } from "./slot";


// Open menu action for non-vr avatars
class ModalMenuAction implements AvatarActionInstance
{
	constructor(avatar : AvatarComponent, input : InputActionDetail | undefined, menuEl : Entity )
	{
		const el = ((input && input.input) || avatar).el;
		const scene = el.sceneEl!;

		const allowedActions : { [action:string] : any } = 
		{
			"Action" : 1,
			"Use" : 1,
			"Grab" : 1,
			"Fire" : 1
		};

		const inputBeginListener = ((event : DetailEvent<InputActionDetail> ) =>
		{
			let action = event.detail.action;
			if(action.startsWith("Left")) action = action.slice(4);
			else if(action.startsWith("Right")) action = action.slice(5);
			
			if( action in allowedActions ) //Skip default input/action system, trigger action directly
			{
				const factory = avatar.system.actions[ event.detail.action ];
				const instance = factory && factory(avatar, event.detail);
				instance && instance.destroy(avatar);
				event.preventDefault();
			}
			
		}) as EventListener;

		//Next input with the same action will close menu :
		const inputEndListener = ((event : DetailEvent<InputActionDetail> ) =>
		{
			if( (event.detail.action == "Menu") && (event.detail !== input) )
				scene.camera.el.emit("close-menu");
		}) as EventListener;

		scene.addEventListener("close-menu", () => {
			scene.removeState("ui-mode");
			scene.emit("exit-ui");
			scene.removeEventListener("input-begin", inputBeginListener, { capture : true });
			scene.removeEventListener("input-end", inputEndListener);
		}, {once:true});

		scene.addEventListener("input-begin", inputBeginListener, { capture : true });
		scene.addEventListener("input-end", inputEndListener);

		
		

		const attachedListener = ((event : SlotEvent) =>
		{
			scene.addEventListener("close-menu", () => {
				menuEl.emit("detach-from-slot", event.detail.slot);
			}, { once : true } );
			scene.addState("ui-mode");
			el.emit("enter-ui");
		}) as EventListener;
		menuEl.addEventListener("attached-to-slot", attachedListener, {once:true});
		menuEl.emit("attach-to-slot", scene.camera.el);
		menuEl.removeEventListener("attached-to-slot", attachedListener);
	}

	tick(avatar : AvatarComponent, t : number, dt : number, wasGrounded : boolean)
	{
		return avatar.el.sceneEl!.is("ui-mode");
	}

	destroy(avatar : AvatarComponent)
	{
	}
}

class VRMenuAction implements AvatarActionInstance
{
	input : InputActionDetail;
	menu : Entity;
	constructor(avatar : AvatarComponent, input : InputActionDetail, menuEl : Entity)
	{
		this.input = input;
		this.menu = menuEl;

		const inputEl = input.input!.el;
		const sceneEl = avatar.el.sceneEl!;

		const attachedListener = ((event : SlotEvent) =>
		{
			const detachMenu = () => menuEl.emit("detach-from-slot", event.detail.slot);
			inputEl.addEventListener("close-menu", detachMenu);
			menuEl.addEventListener("close-menu", detachMenu);
			menuEl.addEventListener("detached-from-slot", () =>
			{
				inputEl.removeEventListener("close-menu", detachMenu);
				menuEl.removeEventListener("close-menu", detachMenu);
			});

		}) as EventListener;

		menuEl.addEventListener("attached-to-slot", attachedListener, {once:true});
		menuEl.emit("attach-to-slot", input.input!.el);
		menuEl.removeEventListener("attached-to-slot", attachedListener);
	}
	tick(avatar : AvatarComponent, t : number, dt : number, wasGrounded : boolean)
	{
		return this.input.amount > 0;
	}
	destroy(avatar: AvatarComponent): void 
	{
		this.menu.emit("close-menu");
	}


}



interface MenuSystemData
{
	main : Entity;
	label : string;
	icon : string;
	action : string;
}
interface MenuSystem extends System
{
	data : MenuSystemData;
}

registerSystem("menu", {
	schema : { 
		main : { type : "selector" },
		label : { type : "string", default : "Menu" },
		icon : { type : "string", default : "" },
		action : { type : "string", default : "Menu" }
	},
	init(this : MenuSystem)
	{
		const avatarSystem = this.el.systems["avatar"] as AvatarSystem;
		this.el.addEventListener("open-menu", (event) =>
		{
			if(!avatarSystem.avatar)
				return;

			if( this.el.is("vr-mode") )
				return;

			const avatarComponent = avatarSystem.avatar.components.avatar;
			const action = new ModalMenuAction(avatarComponent, undefined, event.target as Entity);
			avatarComponent.actions[ this.data.action ] = action;
			event.stopPropagation();
			event.preventDefault();

		}, { capture : true } );
	},
	update(this : MenuSystem, oldData : MenuSystemData)
	{
		const avatarSystem = this.el.systems["avatar"] as AvatarSystem;

		if(oldData && oldData.action)
			delete avatarSystem.actions[oldData.action];
			
		const factory : AvatarActionFactory = (avatar : AvatarComponent, input : InputActionDetail) => 
		{
			if( this.el.is("vr-mode") ) //Vr menu : show next to input.input
				return new VRMenuAction(avatar, input, this.data.main);
			else
				return new ModalMenuAction(avatar, input, this.data.main );
		};
		factory.label = this.data.label;
		factory.icon = this.data.icon;
		avatarSystem.actions[this.data.action] = factory;

	}
});



